import { attach, createStore, sample } from 'effector'
import _ from 'lodash'
import { Batcher } from 'promise-batcher'
import { z } from 'zod'

import type { PublicProfileInfo } from '@portal/portal-api'

import { portalAPI } from '@/shared/api'
import { persist } from '@/shared/lib/persist/local'

export const PublicProfileInfoListParamsSchema = z.union([
    z.object({ publicId: z.array(z.string()).readonly() }),
    z.object({ supportId: z.array(z.string()).readonly() }),
])

export type PublicProfileInfoListParams = z.infer<typeof PublicProfileInfoListParamsSchema>

// 30 minutes
const cacheTime = 30 * 60 * 1000

type CacheEntry = {
    createTime: number
    data: PublicProfileInfo
}

export const $publicProfileCache = createStore<Record<string, CacheEntry>>({})
persist({
    store: $publicProfileCache,
    key: 'wrapper.public-profile-cache',
})

// getPublicProfileListFx is a wrapper around api.profilePublicList that caches results for 30 minutes to avoid excessive api calls
export const getPublicProfileListFx = attach({
    source: $publicProfileCache,
    effect: async (cache, params: PublicProfileInfoListParams): Promise<PublicProfileInfo[]> => {
        const now = Date.now()

        const cachedProfiles: PublicProfileInfo[] = []
        const fetchIds: string[] = []

        const accessor = createAccessor(params)

        for (const profileId of accessor.getProfileIds()) {
            const cacheKey = accessor.getCacheKey(profileId)
            const cacheEntry = cache[cacheKey]
            if (cacheEntry && now - cacheEntry.createTime < cacheTime) {
                cachedProfiles.push(cacheEntry.data)
            } else {
                fetchIds.push(profileId)
            }
        }

        const freshProfiles = await accessor.fetchProfiles(fetchIds)

        return [...cachedProfiles, ...freshProfiles]
    },
})

// update cache on success
sample({
    clock: getPublicProfileListFx.doneData,
    source: $publicProfileCache,
    fn: (cache, result) => {
        const now = Date.now()

        const newCache = { ...cache }

        // cleanup old cache entries
        for (const [cacheKey, cacheEntry] of Object.entries(newCache)) {
            if (now - cacheEntry.createTime > cacheTime) {
                delete newCache[cacheKey]
            }
        }

        // update cache
        for (const profile of result) {
            const cacheEntry = {
                createTime: now,
                data: profile,
            }

            newCache[`publicId:${profile.publicId}`] = cacheEntry
            newCache[`supportId:${profile.supportId}`] = cacheEntry
        }

        return newCache
    },
    target: $publicProfileCache,
})

function createAccessor(params: PublicProfileInfoListParams) {
    const keyPrefix = 'publicId' in params ? 'publicId' : 'supportId'
    // deduplicate ids
    const ids = _.uniq('publicId' in params ? params.publicId : params.supportId)

    return {
        getProfileIds() {
            return ids
        },

        getProfileId(profile: PublicProfileInfo) {
            if (keyPrefix === 'publicId') {
                return profile.publicId
            }

            if (keyPrefix === 'supportId') {
                return profile.supportId
            }

            throw new Error('Invalid params')
        },

        getCacheKey(profileId: string) {
            return `${keyPrefix}:${profileId}`
        },

        fetchProfiles(fetchIds: string[]) {
            if (fetchIds.length === 0) {
                return []
            }

            if (keyPrefix === 'publicId') {
                return portalAPI.profilePublicList({ public_id: fetchIds })
            }

            if (keyPrefix === 'supportId') {
                return portalAPI.profilePublicList({ support_id: fetchIds })
            }

            throw new Error('Invalid params')
        },
    }
}

export async function getPublicProfileByPublicId(publicId: string): Promise<PublicProfileInfo | null> {
    // batcher already has a cache, but we make additional check to avoid waiting for other requests to finish
    const cachedProfile = await getPublicProfileByPublicIdFromCacheFx(publicId)
    if (cachedProfile) {
        return cachedProfile
    }

    return await getPublicProfileByPublicIdBatcher.getResult(publicId)
}

export const getPublicProfileByPublicIdBatcher = new Batcher<string, PublicProfileInfo | null>({
    maxBatchSize: 200,
    queuingDelay: 100,
    batchingFunction: async (publicIdList) => {
        const response = await getPublicProfileListFx({
            publicId: publicIdList,
        })

        const profiles = _.keyBy(response, 'publicId')

        return publicIdList.map((publicId) => profiles[publicId] ?? null)
    },
})

export const getPublicProfileByPublicIdFromCacheFx = attach({
    source: $publicProfileCache,
    effect: (cache, publicId: string): PublicProfileInfo | null => {
        const cacheEntry = cache[`publicId:${publicId}`]
        if (!cacheEntry) {
            return null
        }

        if (Date.now() - cacheEntry.createTime > cacheTime) {
            return null
        }

        return cacheEntry.data
    },
})
